import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useRouter } from 'next/router';

import Tag from 'components/Tag';
import Slider from 'components/sliders/Slider';

// eslint-disable-next-line import/no-cycle
import Block from '../Block';

import styles from './styles.module.scss';

const Tabs = ({ items, prefix, tagsTheme = 'dark' }) => {
	const { asPath, events } = useRouter();
	const [activeIndex, setActiveIndex] = useState(0);
	const ref = useRef(null);
	const sliderRef = useRef(null);

	const sliderTheme = tagsTheme === 'dark' ? 'darkTags' : 'lightTags';

	useEffect(() => {
		const regExp = new RegExp(`#${prefix}-(\\d*)`);
		const changeIndexFromHash = (url) => {
			const pathIndex = +url.match(regExp)?.[1];
			const { top } = ref.current.getBoundingClientRect();

			if (pathIndex && (top < 0 || top >= window.innerHeight)) {
				ref.current.scrollIntoView();
			}

			return setActiveIndex(pathIndex || 0);
		};

		changeIndexFromHash(asPath);

		events.on('hashChangeComplete', changeIndexFromHash);

		return () => {
			events.off('hashChangeComplete', changeIndexFromHash);
		};
	}, []);

	useEffect(() => {
		if (sliderRef.current) {
			sliderRef.current.slideTo(activeIndex);
		}
	}, [activeIndex, sliderRef.current]);

	return (
		<div ref={ref}>
			<Slider
				withNavigation
				theme={sliderTheme}
				navigationIcon="chevronSmall"
				params={{
					spaceBetween: 30,
					freeMode: true,
					centeredSlides: true,
					centeredSlidesBounds: true,
					grabCursor: true
				}}
				getSwiper={(slider) => {
					sliderRef.current = slider;
				}}
			>
				{items.map((tab, index) => (
					<Tag
						key={tab.title}
						title={tab.title}
						theme="tab"
						textTheme={tagsTheme}
						url={`#${prefix}-${index}`}
						active={activeIndex === index}
						className={styles.tab}
						next
					/>
				))}
			</Slider>

			{items.map(({ content }, index) => (
				<div
					// eslint-disable-next-line react/no-array-index-key
					key={`${prefix}-${index}`}
					className={cx(styles.block, activeIndex !== index && styles.hidden)}
				>
					{content.map((block, blockIndex) => (
						// eslint-disable-next-line react/no-array-index-key
						<Block key={`${prefix}-${index}-${blockIndex}`} {...block} />
					))}
				</div>
			))}
		</div>
	);
};

Tabs.propTypes = {
	items: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			content: PropTypes.arrayOf(PropTypes.object).isRequired
		}).isRequired
	).isRequired,
	prefix: PropTypes.string,
	tagsTheme: PropTypes.oneOf(['dark', 'light'])
};

Tabs.defaultProps = {
	prefix: 'tab-item'
};

export default Tabs;
